// Settings → Game. The per-user rules the Master Book (Update Directive,
// rulings 1.5-1.8, 1.11, 2.2-2.3) moved out of hard-coded engine constants:
// session times, week shape, the deity ladder (Update Directive 18 Oct 2026 §2),
// seat window, alignment windows, green bands and the session widget stacks.
// Every change saves through setGameSettings; SyQi's conflict notes show
// gold-rimmed underneath. The old points/season scoring controls are gone.
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles } from '../../v10/theme';
import { Chip, GlassCard, SectionTitle } from '../../v10/ui';
import { TOOL_NAMES } from '../../domain/olmps';
import { useCompassConfig } from '../sectorConfig';
import {
  DEFAULT_GAME_SETTINGS,
  minutesOf,
  normalizeGameSettings,
  settingsConflicts,
  type GameSettings,
  type Weekday,
} from '../gameSettings';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_ORDER: Weekday[] = [1, 2, 3, 4, 5, 6, 0];
const FULL_DAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// settingsConflicts still carries the retired Aim/Goal/Endeavour/season rules;
// keep only its session-time and CLIO notes and add the new ladder/seat rules.
const RETIRED_CONFLICT = /^Your (Aim|Goal|Endeavour)\b/;
function gameConflicts(g: GameSettings): string[] {
  const out = settingsConflicts(g).filter((c) => !RETIRED_CONFLICT.test(c));
  const slots = g.countWeekends ? 7 : g.weekDays.length;
  const L = g.ladder;
  if (L.aimDays > slots) out.push(`Your Aim looks at ${L.aimDays} scheduled Days but your week has only ${slots} counted days.`);
  if (g.seatMinDays > g.seatWindowDays) out.push(`Your seat needs ${g.seatMinDays} days of marks but its window is only ${g.seatWindowDays} days.`);
  const w = g.alignmentWindows;
  if (!(w.aim < w.goal && w.goal < w.endeavour)) out.push('Your alignment windows should grow: Aim shorter than Goal, Goal shorter than Endeavour.');
  if (w.goal < L.goalAims * 7) out.push(`Your Goal alignment window (${w.goal} days) is shorter than its ${L.goalAims} Aims (${L.goalAims * 7} days).`);
  return out;
}

const hhmm = (mins: number) => {
  const m = ((mins % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

function Stepper({
  label,
  hint,
  display,
  onMinus,
  onPlus,
}: {
  label: string;
  hint?: string;
  display: string;
  onMinus: () => void;
  onPlus: () => void;
}) {
  const styles = useStyles();
  return (
    <View style={styles.row}>
      <View style={{ flex: 1 }}>
        <Text style={styles.rowLabel}>{label}</Text>
        {hint ? <Text style={styles.rowHint}>{hint}</Text> : null}
      </View>
      <Pressable onPress={onMinus} hitSlop={6} style={styles.stepBtn} accessibilityLabel={`Decrease ${label}`}>
        <Text style={styles.stepBtnText}>−</Text>
      </Pressable>
      <Text style={styles.stepVal}>{display}</Text>
      <Pressable onPress={onPlus} hitSlop={6} style={styles.stepBtn} accessibilityLabel={`Increase ${label}`}>
        <Text style={styles.stepBtnText}>+</Text>
      </Pressable>
    </View>
  );
}

function StackEditor({
  title,
  stack,
  onChange,
}: {
  title: string;
  stack: string[];
  onChange: (next: string[]) => void;
}) {
  const styles = useStyles();
  // ORACLE is retired and TELOS is the ATLAS widget: one ATLAS entry (telos), no oracle/atlas duplicates.
  const nameOf = (id: string) => (id === 'telos' ? 'ATLAS' : TOOL_NAMES[id] ?? id.toUpperCase());
  const available = Object.keys(TOOL_NAMES).filter((id) => !stack.includes(id) && id !== 'oracle' && id !== 'atlas');
  const move = (i: number) => {
    if (i <= 0) return;
    const next = [...stack];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };
  return (
    <View style={{ marginTop: 6 }}>
      <Text style={styles.rowLabel}>{title}</Text>
      {stack.length === 0 ? <Text style={styles.rowHint}>No widgets — this session will only run its close.</Text> : null}
      {stack.map((id, i) => (
        <View key={id} style={styles.stackRow}>
          <Text style={styles.stackIdx}>{i + 1}</Text>
          <Text style={styles.stackName}>{nameOf(id)}</Text>
          <Pressable onPress={() => move(i)} disabled={i === 0} hitSlop={6} style={[styles.miniBtn, i === 0 && { opacity: 0.25 }]}>
            <Text style={styles.miniBtnText}>↑</Text>
          </Pressable>
          <Pressable onPress={() => onChange(stack.filter((x) => x !== id))} hitSlop={6} style={styles.miniBtn}>
            <Text style={styles.miniBtnText}>✕</Text>
          </Pressable>
        </View>
      ))}
      <Text style={[styles.rowHint, { marginTop: 8 }]}>Add a widget</Text>
      <View style={styles.chipWrap}>
        {available.map((id) => (
          <Chip key={id} small label={`+ ${nameOf(id)}`} onPress={() => onChange([...stack, id])} />
        ))}
      </View>
    </View>
  );
}

export function GameSettingsEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { config, loading, setGameSettings } = useCompassConfig(userId);
  const [g, setG] = useState<GameSettings>(DEFAULT_GAME_SETTINGS);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!loading && !hydrated) {
      setG(config.game);
      setHydrated(true);
    }
  }, [loading, hydrated, config.game]);

  const save = (patch: Partial<GameSettings>) => {
    const next = normalizeGameSettings({ ...g, ...patch });
    setG(next);
    setGameSettings(next);
  };
  const rung = (key: keyof GameSettings['ladder'], d: number) => save({ ladder: { ...g.ladder, [key]: g.ladder[key] + d } });
  const win = (key: keyof GameSettings['alignmentWindows'], d: number) => save({ alignmentWindows: { ...g.alignmentWindows, [key]: g.alignmentWindows[key] + d } });
  const band = (patch: Partial<GameSettings['greenBands']>) => save({ greenBands: { ...g.greenBands, ...patch } });
  const toggleDay = (d: Weekday) => {
    const has = g.weekDays.includes(d);
    if (has && g.weekDays.length === 1) return;
    save({ weekDays: has ? g.weekDays.filter((x) => x !== d) : [...g.weekDays, d] });
  };
  const conflicts = gameConflicts(g);

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <Text style={styles.intro}>
        The rules of your game. Shipped defaults are a Monday–Friday week and a climb of threes — 3 of 4 at every rung; change any of them and SyQi will tell you when
        your settings contradict each other.
      </Text>

      {conflicts.length > 0 ? (
        <View style={styles.conflicts}>
          {conflicts.map((c) => (
            <View key={c} style={styles.conflictNote}>
              <Text style={styles.conflictText}>
                <Text style={styles.conflictWho}>SyQi: </Text>
                {c}
              </Text>
            </View>
          ))}
        </View>
      ) : null}

      <SectionTitle style={styles.gap}>Sessions</SectionTitle>
      <GlassCard style={styles.card}>
        <Stepper
          label="Morning session"
          hint="Charge drains from here, not midnight. Part of the Day orb — NUMA runs here."
          display={g.morningSessionTime}
          onMinus={() => save({ morningSessionTime: hhmm(minutesOf(g.morningSessionTime) - 15) })}
          onPlus={() => save({ morningSessionTime: hhmm(minutesOf(g.morningSessionTime) + 15) })}
        />
        <Stepper
          label="Evening session"
          hint="Opens Revise and closes the day. With the morning session it completes the Day orb."
          display={g.eveningSessionTime}
          onMinus={() => save({ eveningSessionTime: hhmm(minutesOf(g.eveningSessionTime) - 15) })}
          onPlus={() => save({ eveningSessionTime: hhmm(minutesOf(g.eveningSessionTime) + 15) })}
        />
      </GlassCard>

      <SectionTitle style={styles.gap}>Week shape</SectionTitle>
      <GlassCard style={styles.card}>
        <Text style={styles.rowLabel}>Counted days</Text>
        <View style={styles.chipWrap}>
          {DAY_ORDER.map((d) => (
            <Chip key={d} small label={DAY_NAMES[d]} active={g.weekDays.includes(d)} onPress={() => toggleDay(d)} />
          ))}
        </View>
        <View style={[styles.row, { marginTop: 10 }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.rowLabel}>Count weekends</Text>
            <Text style={styles.rowHint}>Weekend days count toward windows even when outside the counted days.</Text>
          </View>
          <ThemedSwitch value={g.countWeekends} onValueChange={(v) => save({ countWeekends: v })} />
        </View>
        <Text style={[styles.rowLabel, { marginTop: 10 }]}>Week boundary day</Text>
        <Text style={styles.rowHint}>The evening session on this day closes the week and opens the next.</Text>
        <View style={styles.chipWrap}>
          {DAY_ORDER.map((d) => (
            <Chip key={d} small label={FULL_DAY[d].slice(0, 3)} active={g.weekBoundaryDay === d} onPress={() => save({ weekBoundaryDay: d })} />
          ))}
        </View>
      </GlassCard>

      <SectionTitle style={styles.gap}>The climb</SectionTitle>
      <GlassCard style={styles.card}>
        <Text style={styles.rowHint}>Every deity is acquired by climbing the same ladder. A Day only counts when its Day orb (morning + evening session) is done.</Text>
        <Stepper label="Day: actions required" hint="How many required actions a Day has." display={String(g.ladder.dayActions)} onMinus={() => rung('dayActions', -1)} onPlus={() => rung('dayActions', 1)} />
        <Stepper label="Day: actions needed" hint={`${g.ladder.dayThreshold} of ${g.ladder.dayActions} actions`} display={String(g.ladder.dayThreshold)} onMinus={() => rung('dayThreshold', -1)} onPlus={() => rung('dayThreshold', 1)} />
        <Stepper label="Aim: scheduled Days" hint="Days in the week the Aim looks at." display={String(g.ladder.aimDays)} onMinus={() => rung('aimDays', -1)} onPlus={() => rung('aimDays', 1)} />
        <Stepper label="Aim: Days needed" hint={`${g.ladder.aimThreshold} of ${g.ladder.aimDays} Days`} display={String(g.ladder.aimThreshold)} onMinus={() => rung('aimThreshold', -1)} onPlus={() => rung('aimThreshold', 1)} />
        <Stepper label="Goal: Aims in the block" hint="A Goal is a fixed 4-week block." display={String(g.ladder.goalAims)} onMinus={() => rung('goalAims', -1)} onPlus={() => rung('goalAims', 1)} />
        <Stepper label="Goal: Aims needed" hint={`${g.ladder.goalThreshold} of ${g.ladder.goalAims} Aims`} display={String(g.ladder.goalThreshold)} onMinus={() => rung('goalThreshold', -1)} onPlus={() => rung('goalThreshold', 1)} />
        <Stepper label="Acquire at (Goals)" hint="Goals earned before the deity is acquired." display={String(g.ladder.goalsToAcquire)} onMinus={() => rung('goalsToAcquire', -1)} onPlus={() => rung('goalsToAcquire', 1)} />
      </GlassCard>

      <SectionTitle style={styles.gap}>Seat</SectionTitle>
      <GlassCard style={styles.card}>
        <Text style={styles.rowHint}>The seat is what you have actually been doing lately: deity marks against malady marks over a rolling window.</Text>
        <Stepper label="Seat window (days)" display={String(g.seatWindowDays)} onMinus={() => save({ seatWindowDays: g.seatWindowDays - 1 })} onPlus={() => save({ seatWindowDays: g.seatWindowDays + 1 })} />
        <Stepper label="Minimum days of marks" hint="Until then the seat shows —." display={String(g.seatMinDays)} onMinus={() => save({ seatMinDays: g.seatMinDays - 1 })} onPlus={() => save({ seatMinDays: g.seatMinDays + 1 })} />
      </GlassCard>

      <SectionTitle style={styles.gap}>Alignment windows</SectionTitle>
      <GlassCard style={styles.card}>
        <Text style={styles.rowHint}>Each STAGE orb scores a sector's alignment over its own window. The Today orb is always the day itself.</Text>
        <Stepper label="Aim (days)" display={String(g.alignmentWindows.aim)} onMinus={() => win('aim', -1)} onPlus={() => win('aim', 1)} />
        <Stepper label="Goal (days)" display={String(g.alignmentWindows.goal)} onMinus={() => win('goal', -7)} onPlus={() => win('goal', 7)} />
        <Stepper label="Endeavour (days)" display={String(g.alignmentWindows.endeavour)} onMinus={() => win('endeavour', -7)} onPlus={() => win('endeavour', 7)} />
      </GlassCard>

      <SectionTitle style={styles.gap}>Green bands</SectionTitle>
      <GlassCard style={styles.card}>
        <Text style={styles.rowHint}>NUMA keeps Charge, Load and Flow in the green. Green is the target NUMA steers you toward each day.</Text>
        <Stepper
          label="Charge at least"
          display={g.greenBands.chargeMin.toFixed(1)}
          onMinus={() => band({ chargeMin: Math.round((g.greenBands.chargeMin - 0.5) * 10) / 10 })}
          onPlus={() => band({ chargeMin: Math.round((g.greenBands.chargeMin + 0.5) * 10) / 10 })}
        />
        <Stepper
          label="Load at most (× your baseline)"
          display={`×${g.greenBands.loadMaxOfBaseline.toFixed(2)}`}
          onMinus={() => band({ loadMaxOfBaseline: Math.round((g.greenBands.loadMaxOfBaseline - 0.05) * 100) / 100 })}
          onPlus={() => band({ loadMaxOfBaseline: Math.round((g.greenBands.loadMaxOfBaseline + 0.05) * 100) / 100 })}
        />
        <Stepper
          label="Flow at least"
          display={String(g.greenBands.flowMin)}
          onMinus={() => band({ flowMin: g.greenBands.flowMin - 5 })}
          onPlus={() => band({ flowMin: g.greenBands.flowMin + 5 })}
        />
      </GlassCard>

      <SectionTitle style={styles.gap}>Session widgets</SectionTitle>
      <GlassCard style={styles.card}>
        <StackEditor title="Evening session (CLIO by default)" stack={g.eveningStack} onChange={(eveningStack) => save({ eveningStack })} />
        <View style={styles.sep} />
        <StackEditor title="Morning session (NUMA · ATLAS · KYRS by default)" stack={g.morningStack} onChange={(morningStack) => save({ morningStack })} />
      </GlassCard>
    </ScrollView>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
    content: { paddingHorizontal: 16, paddingBottom: 48, gap: 8 },
    intro: { fontSize: 12, color: t.ivorySoft, fontStyle: 'italic', marginTop: 4 },
    gap: { marginTop: 14 },
    card: { borderRadius: radii.tile, padding: 12, gap: 6 },
    row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 4 },
    rowLabel: { fontSize: 14, fontWeight: '700', color: t.ivory },
    rowHint: { fontSize: 11.5, color: t.ivorySoft, marginTop: 2 },
    stepBtn: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: t.gold,
      backgroundColor: t.glass,
    },
    stepBtnText: { fontSize: 18, fontWeight: '800', color: t.goldBright, lineHeight: 20 },
    stepVal: { minWidth: 52, textAlign: 'center', fontSize: 15, fontWeight: '800', color: t.ivory },
    chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
    stackRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 4 },
    stackIdx: { width: 18, color: t.ivoryFaint, fontWeight: '800', fontSize: 12 },
    stackName: { flex: 1, color: t.ivory, fontWeight: '700', fontSize: 14 },
    miniBtn: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: t.lineFaint },
    miniBtnText: { color: t.goldBright, fontWeight: '900', fontSize: 12 },
    sep: { height: 1, backgroundColor: t.lineFaint, marginVertical: 10 },
    conflicts: { gap: 6, marginTop: 6 },
    conflictNote: {
      borderRadius: radii.tile,
      borderWidth: 1.5,
      borderColor: t.gold,
      backgroundColor: t.glass,
      paddingVertical: 8,
      paddingHorizontal: 12,
      boxShadow: `0 0 12px ${t.goldGlow}`,
    },
    conflictText: { fontSize: 12.5, color: t.ivory, lineHeight: 17 },
    conflictWho: { fontWeight: '900', color: t.goldBright },
  }),
);
